import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import { Space, Button } from 'antd';
import { useEditor, EditorContent, JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';

import './basicHTMLEditor.scss';
import { ComponentProps } from 'react';

type BasicHTMLEditorProps = {
  onChange?: (content: JSONContent) => void;
  value?: JSONContent;
  editable?: boolean;
} & Omit<
  ComponentProps<typeof EditorContent>,
  'className' | 'editor' | 'content' | 'value'
>;

export type HTMLEditorContent = JSONContent;

const BasicHTMLEditor = ({
  value,
  onChange,
  editable = true,
  ...editorProps
}: BasicHTMLEditorProps) => {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    editable,
    content: value,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getJSON());
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <Space
      className="basic-html-editor"
      direction="vertical"
      style={{ display: 'flex' }}
    >
      {editable && (
        <Space direction="horizontal">
          <Button
            disabled={!editor.can().chain().focus().toggleBold().run()}
            onClick={() => editor.chain().focus().toggleBold().run()}
            type={editor.isActive('bold') ? 'primary' : 'default'}
            icon={<BoldOutlined />}
          />
          <Button
            icon={<ItalicOutlined />}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            type={editor.isActive('italic') ? 'primary' : 'default'}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <Button
            disabled={!editor.can().chain().focus().toggleUnderline().run()}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            icon={<UnderlineOutlined />}
            type={editor.isActive('underline') ? 'primary' : 'default'}
          />
          <Button
            icon={<UnorderedListOutlined />}
            disabled={!editor.can().chain().focus().toggleBulletList().run()}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            type={editor.isActive('bulletList') ? 'primary' : 'default'}
          />
          <Button
            icon={<OrderedListOutlined />}
            disabled={!editor.can().chain().focus().toggleOrderedList().run()}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            type={editor.isActive('orderedList') ? 'primary' : 'default'}
          />
        </Space>
      )}
      <EditorContent
        {...editorProps}
        className="editor-content"
        editor={editor}
      />
    </Space>
  );
};

export default BasicHTMLEditor;
